// src/features/A3Metadata/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import i18n from "@/i18n";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

// Import select options from the filter file (do not modify filter.tsx)
import { fieldKeyOptions } from "./filter";

const client = "a3";
const table_name = "a3_metadata";

// Zod schema: make fields optional and nullable to match the form behavior
const FormSchema = z.object({
  owner_id: z.coerce.number().optional().nullable(),
  owner_type: z.string().nullable().optional(),
  field_key: z.string().nullable().optional(),
  field_value: z.string().nullable().optional(),
});

export function A3MetadataNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
    // No need for special handling since we're using simple text fields
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t("a3_metadata.title", {
                  ns: "tables",
                  defaultValue: "A3 Metadata",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Owner fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="owner_type"
                    label={i18n.t("a3_metadata.columns.owner_type", {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="owner_id"
                    label={i18n.t("a3_metadata.columns.owner_id", {
                      ns: "tables",
                      defaultValue: "Owner ID",
                    })}
                    type="number"
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Main inputs: Field Key and Field Value */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="field_key"
                    label={i18n.t("a3_metadata.columns.field_key", {
                      ns: "tables",
                      defaultValue: "Field Key",
                    })}
                    options={fieldKeyOptions}
                    control={form.control}
                    isRequired
                    allow_empty_option={false}
                  />
                  <InputField
                    name="field_value"
                    label={i18n.t("a3_metadata.columns.field_value", {
                      ns: "tables",
                      defaultValue: "Field Value",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_create")}
          >
            {changedFields
              ? // Exclude owner_id and owner_type from diff display
                NewDiff(changedFields, [])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
